/**
 * Veluna inline article capture (2026-10-01).
 *
 * One compact email box inside every journal article, inserted right after
 * the direct answer (p.veluna-direct-answer) when the article has one, else
 * after the first paragraph of the first visible section (the paragraph that
 * answers the title). It is part of the page, not an overlay: no dismissal
 * memory, no cooldown, no capture-guard arbitration (the guard arbitrates
 * interruptions; this interrupts nothing). Never inside a table, list, note
 * or hidden block. Never on the journal index or any non-article page.
 *
 * Captured visitors see nothing. It reads the same flags every other capture
 * on the site reads (veluna_email_captured, veluna_pdf_captured,
 * veluna_quiz_email / veluna_quiz_email_captured) and writes the same flags
 * on a 2xx, so nobody who signs up here is asked again anywhere else.
 *
 * Posts the popup's body shape (veluna-pdf-capture.js) to the intake with
 * source 'inline_article' and surface 'article'. layout/theme.liquid's fetch
 * hook also pushes generate_lead on a 2xx; the push here lands first, so the
 * hook dedupes. The intake may answer with signup_code / signup_ends; without
 * them the success state shows SLEEP10, exactly as the popup does.
 *
 * Topic (by handle keyword) decides the headline and the follow-up link.
 * Concern code (mc / dr / ss) comes from window.VELUNA_BLOG_CLUSTER when the
 * article sets it, else from the topic, so the welcome email carries the
 * right guide.
 *
 * ?inline_preview=1 bypasses the captured check so the owner can see the box
 * on a browser that has signed up before.
 */
(function () {
  'use strict';
  if (window.__vlnInlineCapture) return;
  window.__vlnInlineCapture = true;

  var ENDPOINT = 'https://ary09mimgg.execute-api.us-east-2.amazonaws.com/Prod/subscribe';
  var VARIANT = 'inline_article_v1';
  var SEQUENCE = 'welcome';
  var ID = 'veluna-inline-capture';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var PREVIEW = /[?&]inline_preview=1\b/.test(window.location.search);

  /* Same three rituals and guide files as veluna-pdf-capture.js. */
  var CONCERNS = {
    mc: { ritual: 'Mind Calm', product: '/products/mind-calm-ritual-set',
      guide: 'https://cdn.shopify.com/s/files/1/0660/1372/4750/files/Veluna-Mind-Calm-Sleep-Guide_498c431a-e208-4dc2-a82f-ecfe391e66ee.pdf?v=1790031056' },
    dr: { ritual: 'Deep Restore', product: '/products/deep-restore-ritual-set',
      guide: 'https://cdn.shopify.com/s/files/1/0660/1372/4750/files/Veluna-Deep-Restore-Sleep-Guide_4b0e977e-e588-46b3-a770-60a1a3dea23d.pdf?v=1790031062' },
    ss: { ritual: 'Sacred Soothe', product: '/products/sacred-soothe-ritual-set-1',
      guide: 'https://cdn.shopify.com/s/files/1/0660/1372/4750/files/Veluna-Sacred-Soothe-Sleep-Guide_6e6345f9-2f2e-434f-b89d-4fe916f28d47.pdf?v=1790031067' }
  };

  /* First match wins, in this order. `alt` is used when `link` is the page
     the reader is already on. `noun` fills the promise line. */
  var TOPICS = [
    { key: 'magnesium', test: /magnesium/, concern: 'mc', noun: 'table',
      headline: 'Get the dose table and 10% off',
      link: '/blogs/journal/sleep-supplement-doses-used-in-trials', linkLabel: 'Open the dose table',
      alt: '/blogs/journal/best-magnesium-for-sleep-how-to-choose', altLabel: 'Open the magnesium guide' },
    { key: 'melatonin', test: /melatonin/, concern: 'mc', noun: 'chart',
      headline: 'Choosing a dose? Get the dose chart and 10% off',
      link: '/blogs/journal/low-dose-melatonin-how-to-choose-a-dose', linkLabel: 'Open the dose chart',
      alt: '/blogs/journal/sleep-supplement-doses-used-in-trials', altLabel: 'Open the dose reference table' },
    { key: 'biotin', test: /biotin|hair-skin|gummies/, concern: 'ss', noun: 'checklist',
      headline: 'Get the gummies checklist and 10% off',
      link: '/blogs/journal/hair-skin-and-nails-gummies-what-to-check', linkLabel: 'Open the gummies checklist',
      alt: '/blogs/journal/biotin-at-night-benefits-results', altLabel: 'Open the biotin guide' },
    { key: 'collagen', test: /collagen/, concern: 'dr', noun: 'guide',
      headline: 'Get the collagen guide and 10% off',
      link: '/blogs/journal/how-to-choose-a-collagen-powder', linkLabel: 'Open the collagen guide',
      alt: '/blogs/journal/how-to-choose-a-collagen-night-cream', altLabel: 'Open the collagen night cream guide' },
    { key: 'ashwagandha', test: /ashwagandha/, concern: 'mc', noun: 'guide',
      headline: 'Get the ashwagandha guide and 10% off',
      link: '/blogs/journal/how-to-choose-an-ashwagandha-supplement', linkLabel: 'Open the ashwagandha guide',
      alt: '/blogs/journal/best-ashwagandha-for-sleep', altLabel: 'Open the ashwagandha picks' }
  ];
  /* Default: the guide link is the ritual's own PDF, the same file the popup opens. */
  var DEFAULT_TOPIC = { key: 'default', concern: 'mc', noun: 'guide',
    headline: 'Get the sleep guide and 10% off', link: null, linkLabel: 'Open the sleep guide' };

  function ls(k) { try { return localStorage.getItem(k); } catch (_) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (_) {} }
  function captured() {
    return ls('veluna_email_captured') === 'true' || !!ls('veluna_pdf_captured') ||
      ls('veluna_quiz_email_captured') === 'true' ||
      EMAIL_RE.test((ls('veluna_quiz_email') || '').trim());
  }
  function markCaptured(email) {
    lsSet('veluna_email_captured', 'true');
    lsSet('veluna_pdf_captured', email + '|' + Date.now());
    lsSet('veluna_quiz_email', email);
    lsSet('veluna_quiz_email_captured', 'true');
  }
  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function dl(ev) { try { (window.dataLayer = window.dataLayer || []).push(ev); } catch (_) {} }
  function ga(name, p) { try { if (typeof window.gtag === 'function') window.gtag('event', name, p); } catch (_) {} }

  function articlePath() {
    var m = /\/blogs\/([^/]+)\/([^/?#]+)\/?$/.exec(window.location.pathname);
    if (!m) return null;
    var handle = m[2];
    try { handle = decodeURIComponent(handle); } catch (_) {}
    return { blog: m[1], handle: handle.toLowerCase() };
  }
  function pickTopic(handle) {
    for (var i = 0; i < TOPICS.length; i++) if (TOPICS[i].test.test(handle)) return TOPICS[i];
    return DEFAULT_TOPIC;
  }
  function concernCode(topic) {
    var id = String(window.VELUNA_BLOG_CLUSTER || '').toLowerCase();
    return CONCERNS[id] ? id : topic.concern;
  }

  /* ---- placement ---- */
  var HIDDEN_SEL = '.veluna-editorial-note,.veluna-key-takeaways,.veluna-author-byline';
  var NO_HOST_SEL = 'table,ul,ol,dl,figure,blockquote,aside,details,nav,form,.veluna-cta,.veluna-cta-block,.veluna-toc,.van,' + HIDDEN_SEL;
  function visible(el) {
    if (!el || el.closest(HIDDEN_SEL)) return false;
    var n = el;
    while (n && n.nodeType === 1 && n !== document.body) {
      var cs = window.getComputedStyle(n);
      if (cs.display === 'none' || cs.visibility === 'hidden') return false;
      n = n.parentElement;
    }
    return true;
  }
  function isAnchorP(el) {
    if (!el || el.tagName !== 'P' || el.closest(NO_HOST_SEL) || !visible(el)) return false;
    if (/\bveluna-(ingredient-disclosure|where-to-buy|author-byline)\b/.test(el.className || '')) return false;
    return (el.textContent || '').replace(/\s+/g, ' ').trim().length >= 40;
  }
  function findAnchor(root) {
    var da = root.querySelector('.veluna-direct-answer');
    if (da && !da.closest(NO_HOST_SEL) && visible(da)) return da;
    var h2s = root.querySelectorAll('h2'), tried = 0;
    for (var i = 0; i < h2s.length && tried < 3; i++) {
      var h2 = h2s[i];
      if (!visible(h2) || h2.closest(NO_HOST_SEL)) continue;
      var t = (h2.textContent || '').toLowerCase();
      if (/key takeaways|frequently asked|^\s*faqs?\s*$/.test(t)) continue;
      tried++;
      var sib = h2.nextElementSibling;
      while (sib && sib.tagName !== 'H2') {
        if (isAnchorP(sib)) return sib;
        sib = sib.nextElementSibling;
      }
    }
    /* No section paragraph (bare-text bodies): after the second top-level paragraph. */
    var ps = [], kids = root.children;
    for (var k = 0; k < kids.length; k++) if (isAnchorP(kids[k])) ps.push(kids[k]);
    return ps.length >= 3 ? ps[1] : null;
  }

  /* ---- views ---- */
  function viewForm(topic) {
    return '' +
      '<p class="vic__eyebrow">Reader offer</p>' +
      '<p class="vic__title" role="heading" aria-level="3" id="vic-title">' + esc(topic.headline) + '</p>' +
      '<p class="vic__promise">Your 10% off code appears here, the ' + esc(topic.noun) + ' link too, and the sleep guide lands in your inbox.</p>' +
      '<form class="vic__form" novalidate>' +
        '<label class="vic__sr" for="vic-email">Email address</label>' +
        '<input class="vic__input" id="vic-email" type="email" name="email" placeholder="you@example.com" autocomplete="email" inputmode="email" required>' +
        '<button class="vic__btn" type="submit">Get my code</button>' +
      '</form>' +
      '<p class="vic__status" role="status" aria-live="polite"></p>' +
      '<p class="vic__fine">One email a week at most. Unsubscribe any time.</p>';
  }
  function followUp(topic, concern) {
    var href = topic.link, label = topic.linkLabel, ext = false;
    if (!href) { href = CONCERNS[concern].guide; ext = true; }
    else if (href.replace(/\/$/, '') === window.location.pathname.replace(/\/$/, '')) { href = topic.alt; label = topic.altLabel; }
    return { href: href, label: label, ext: ext };
  }
  function viewSuccess(topic, concern, email, j) {
    var code = j && j.signup_code ? String(j.signup_code) : 'SLEEP10';
    var note = j && j.signup_code
      ? 'Your own code' + (j.signup_ends ? ', works until ' + esc(j.signup_ends) : '') + '. Use it at checkout on your first set or the first delivery of a plan.'
      : 'Use it at checkout on your first set or the first delivery of a plan. Once per customer.';
    var f = followUp(topic, concern);
    return '' +
      '<p class="vic__eyebrow" tabindex="-1" data-focus>Your 10% off code, ready now</p>' +
      '<div class="vic__code"><span class="vic__code-value" data-code>' + esc(code) + '</span>' +
        '<button type="button" class="vic__copy" data-copy aria-label="Copy the code">Copy</button></div>' +
      '<p class="vic__code-note">' + note + '</p>' +
      '<p class="vic__next"><a class="vic__link" href="' + esc(f.href) + '"' + (f.ext ? ' target="_blank" rel="noopener"' : '') + '>' + esc(f.label) + '</a></p>' +
      '<p class="vic__sent">The sleep guide is on its way to ' + esc(email) + '.</p>';
  }

  /* ---- submit ---- */
  function post(email, topic, concern, handle) {
    var c = CONCERNS[concern];
    return fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email,
        topic: 'popup_subscribe',
        want_code: true,
        concern: concern,
        first_name: '',
        source: 'inline_article',
        surface: 'article',
        email_sequence: SEQUENCE,
        popup_variant: VARIANT,
        tags: ['inline_article', 'concern_' + concern, SEQUENCE, 'surface_article', 'topic_' + topic.key],
        fields: { sleep_concern: concern, ritual_type: c.ritual, email_sequence: SEQUENCE,
                  ritual_guide_url: c.guide, popup_variant: VARIANT,
                  article_topic: topic.key, article_handle: handle },
        metadata: { page: window.location.pathname, referrer: document.referrer || '',
                    user_agent: (navigator && navigator.userAgent) || '' }
      })
    });
  }

  function wire(box, topic, concern, handle) {
    var inner = box.querySelector('.vic__inner');
    var form = inner.querySelector('form');
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var input = form.querySelector('input[type="email"]');
      var btn = form.querySelector('button[type="submit"]');
      var status = inner.querySelector('.vic__status');
      var email = (input.value || '').trim();
      status.textContent = '';
      if (!EMAIL_RE.test(email)) { status.textContent = 'Please enter a full email address.'; input.focus(); return; }
      btn.disabled = true; btn.textContent = 'Sending';
      /* Lock the height so the success view never collapses the box. */
      box.style.minHeight = Math.max(box.offsetHeight, parseFloat(getComputedStyle(box).minHeight) || 0) + 'px';
      post(email, topic, concern, handle).then(function (res) {
        if (!res || !res.ok) throw new Error('intake ' + (res && res.status));
        return res.json().catch(function () { return {}; });
      }).then(function (j) {
        markCaptured(email);
        dl({ event: 'generate_lead', source: 'inline_article', concern: concern, surface: 'article' });
        ga('generate_lead', { source: 'inline_article', concern: concern, surface: 'article' });
        dl({ event: 'veluna_lead_captured', sleep_concern: concern, ritual_type: CONCERNS[concern].ritual, surface: 'article', source: 'inline_article' });
        inner.innerHTML = viewSuccess(topic, concern, email, j);
        box.setAttribute('data-state', 'success');
        var copy = inner.querySelector('[data-copy]');
        if (copy) copy.addEventListener('click', function () {
          var code = inner.querySelector('[data-code]');
          var done = function () { copy.textContent = 'Copied'; setTimeout(function () { copy.textContent = 'Copy'; }, 2000); };
          try {
            if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(code.textContent).then(done, function () {});
            else { var r = document.createRange(); r.selectNodeContents(code); var s = window.getSelection(); s.removeAllRanges(); s.addRange(r); done(); }
          } catch (_) {}
        });
        var f = inner.querySelector('[data-focus]');
        if (f) { try { f.focus({ preventScroll: true }); } catch (_) {} }
      }).catch(function () {
        status.textContent = 'Something went wrong, please try again.';
        btn.disabled = false; btn.textContent = 'Get my code';
      });
    });
  }

  function run() {
    if (document.getElementById(ID)) return;
    var art = articlePath();
    if (!art) return;
    var root = document.querySelector('.article__content');
    if (!root) return;
    if (captured() && !PREVIEW) return;
    var anchor = findAnchor(root);
    if (!anchor) return;
    var topic = pickTopic(art.handle);
    var concern = concernCode(topic);
    var box = document.createElement('aside');
    box.id = ID;
    box.className = 'vic';
    box.setAttribute('data-topic', topic.key);
    box.setAttribute('data-concern', concern);
    box.setAttribute('data-state', 'form');
    box.setAttribute('aria-labelledby', 'vic-title');
    box.innerHTML = '<div class="vic__inner">' + viewForm(topic) + '</div>';
    anchor.parentNode.insertBefore(box, anchor.nextSibling);
    wire(box, topic, concern, art.handle);
    dl({ event: 'veluna_inline_capture_shown', topic: topic.key, concern: concern, article: art.handle });
    if ('IntersectionObserver' in window) {
      var seen = false;
      new IntersectionObserver(function (es, o) {
        es.forEach(function (e) {
          if (e.isIntersecting && !seen) {
            seen = true; o.disconnect();
            dl({ event: 'veluna_inline_capture_view', topic: topic.key, concern: concern, article: art.handle });
            ga('inline_capture_exposure', { article_handle: art.handle, topic: topic.key, concern: concern });
          }
        });
      }, { threshold: 0.5 }).observe(box);
    }
  }

  /* With `defer` this runs before DOMContentLoaded; the listener below then
     fires after the theme's own article fixers (registered earlier), so
     visibility is judged on the final DOM. `load` is a harmless safety net. */
  if (document.readyState === 'complete') run();
  else { document.addEventListener('DOMContentLoaded', run); window.addEventListener('load', run); }
})();
